import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

interface PageTitleMapping {
  [key: string]: string;
}

// Define route-to-title mappings
const pageTitleMappings: PageTitleMapping = {
  '/insights/home': 'app.pageTitle.home',
  '/insights/academic_office': 'app.pageTitle.academicsAndOffices',
  '/insights/settings': 'app.pageTitle.settings',
  '/insights/unauthorized': 'app.pageTitle.unauthorized',
};

// Pattern-based mappings for dynamic routes
const getPatternBasedTitle = (pathname: string): string | null => {
  if (pathname.match(/^\/insights\/institution\/[^\/]+$/)) {
    return 'app.pageTitle.institutionDetails';
  }
  if (pathname.match(/^\/insights\/institution\/[^\/]+\/organization\/[^\/]+$/)) {
    return 'app.pageTitle.organizationInsights';
  }
  if (pathname.match(/^\/insights\/institution\/[^\/]+\/program\/[^\/]+$/)) {
    return 'app.pageTitle.programInsights';
  }
  if (pathname.match(/^\/insights\/planning\/institution\/[^\/]+\/organization\/[^\/]+$/)) {
    return 'app.pageTitle.planningOrganization';
  }
  if (pathname.match(/^\/insights\/.*\/html$/)) {
    return 'app.pageTitle.exportView';
  }
  if (pathname.match(/^\/insights\/planning\/institution\/[^\/]+$/)) {
    return 'app.pageTitle.planning.institutionDetails';
  }
  return null;
};

export const usePageTitle = (customTitle?: string) => {
  const location = useLocation();
  const intl = useIntl();

  useEffect(() => {
    const updateTitle = () => {
      const insightsHub = intl.formatMessage({ id: 'app.pageTitle.insightsHub' });
      let pageTitle = insightsHub; // Default fallback

      if (customTitle) {
        // If a custom title is provided, use it
        pageTitle = `${customTitle} - ${insightsHub}`;
      } else {
        // Try to get title from route mappings
        const mappedTitleKey = pageTitleMappings[location.pathname];

        if (mappedTitleKey) {
          const translatedTitle = intl.formatMessage({ id: mappedTitleKey });
          pageTitle = `${translatedTitle} - ${insightsHub}`;
        } else {
          // Try pattern-based matching
          const patternTitleKey = getPatternBasedTitle(location.pathname);
          if (patternTitleKey) {
            const translatedTitle = intl.formatMessage({ id: patternTitleKey });
            pageTitle = `${translatedTitle} - ${insightsHub}`;
          } else {
            // Try to extract from page header element
            const titleElement = document.querySelector('.page-header .title');
            if (titleElement?.textContent) {
              pageTitle = `${titleElement.textContent} - ${insightsHub}`;
            }
          }
        }
      }

      document.title = pageTitle;
    };

    // Update title immediately
    updateTitle();

    // Also update after a short delay to catch dynamically loaded content
    const timeoutId = setTimeout(updateTitle, 100);

    return () => clearTimeout(timeoutId);
  }, [location.pathname, customTitle, intl]);
};